import React, { Children } from 'react';
import { CheckIcon, BookOpenIcon, LayoutDashboardIcon, MessageCircleHeartIcon, LifeBuoyIcon, UsersIcon, XIcon, CalculatorIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
import { TopBar } from '../components/TopBar';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
export function FamiliesPage() {
  return <div className="flex flex-col min-h-screen w-full" data-id="element-1269">
      <TopBar data-id="element-1270" />
      <Header data-id="element-1271" />

      {/* Hero Section */}
      <section className="w-full bg-white py-20 md:py-28 border-b border-gray-200" data-id="element-1272">
        <div className="max-w-7xl mx-auto px-6 md:px-12" data-id="element-1273">
          <div className="flex flex-col lg:flex-row items-center gap-12" data-id="element-1274">
            {/* Left: Copy */}
            <div className="flex-1 text-left" data-id="element-1275">
              <div className="inline-block bg-[#631bb7] bg-opacity-10 text-[#631bb7] px-4 py-2 rounded-full text-sm font-semibold mb-6" data-id="element-1276">
                For Families
              </div>
              <h1 className="mb-6 text-4xl md:text-5xl lg:text-6xl font-extrabold text-[#111111] leading-tight" data-id="element-1277">
                Your Parents Just Clicked
                <br data-id="element-1278" />
                Another Sketchy Link
              </h1>
              <p className="text-xl md:text-2xl text-gray-600 leading-relaxed mb-10 max-w-2xl" data-id="element-1279">
                Aura can't teach your mom why that email was fake. Life360 can't
                help your dad understand what two-factor authentication actually
                does.{' '}
                <strong className="text-[#111111]" data-id="element-1280">
                  Caveo is the patient tech expert your family never had
                </strong>{' '}
                — no surveillance required.
              </p>
              <div className="flex flex-col sm:flex-row items-start gap-4 mb-4" data-id="element-1281">
                <Link to="/onboarding" className="bg-[#631bb7] text-white px-8 py-4 rounded-lg font-bold no-underline hover:bg-[#521099] transition-all shadow-md" data-id="element-1282">
                  See How It Works
                </Link>
              </div>
              <p className="text-gray-500" data-id="element-1283">
                One subscription. Whole family. Zero surveillance.
              </p>
            </div>

            {/* Right: Photo */}
            <div className="flex-1 w-full lg:max-w-[500px]" data-id="element-1284">
              <div className="rounded-2xl overflow-hidden shadow-xl" data-id="element-1285">
                <img src="https://cdn.magicpatterns.com/uploads/ni76AqBzVEWfwitJfYbfKB/family-photo.png" alt="A grandmother and granddaughter hugging and laughing together" className="w-full h-full object-cover" data-id="element-1286" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Problem/Agitation Section */}
      <section className="w-full bg-[#f8f8f8] py-16 md:py-24" data-id="element-1287">
        <div className="max-w-7xl mx-auto px-6 md:px-12" data-id="element-1288">
          <div className="max-w-3xl mx-auto" data-id="element-1289">
            <h2 className="text-3xl md:text-4xl font-bold text-[#111111] mb-8 text-center" data-id="element-1290">
              You Shouldn't Be Your Family's Unpaid IT Department
            </h2>

            <div className="space-y-6 text-lg text-gray-600 leading-relaxed" data-id="element-1291">
              <p className="text-xl text-[#111111] font-medium" data-id="element-1292">
                Every Sunday, the call comes.
              </p>

              <div className="bg-white rounded-xl p-6 border-2 border-gray-200 space-y-3 italic text-gray-500" data-id="element-1293">
                <p data-id="element-1294">"The computer says my password is wrong."</p>
                <p data-id="element-1295">"Netflix isn't working."</p>
                <p data-id="element-1296">"I got an email from Amazon but I didn't order anything."</p>
                <p data-id="element-1297">"What's this charge on my credit card?"</p>
              </div>

              <p className="font-semibold text-[#111111]" data-id="element-1298">
                You've tried everything.
              </p>

              <p data-id="element-1299">
                You bought your parents McAfee (<strong data-id="element-1300">$109/year</strong>)
                because that's what Best Buy recommended. You set up Life360 (
                <strong data-id="element-1301">$300/year</strong>) so you know Mom got home safe. You
                even paid for Aura's $32/month family plan (
                <strong data-id="element-1302">$384/year</strong>) with the VPN they'll never turn on
                and the password manager they'll never use.
              </p>

              <div className="bg-red-50 rounded-xl p-6 border-2 border-red-100" data-id="element-1303">
                <p className="text-[#111111] font-bold text-xl mb-2" data-id="element-1304">
                  Three subscriptions. $793/year.
                </p>
                <p className="text-gray-600 mb-0" data-id="element-1305">
                  And your dad still asks why he needs "that extra password
                  thing" every single time.
                </p>
              </div>

              <p data-id="element-1306">
                <strong className="text-[#111111]" data-id="element-1307">
                  Here's what nobody tells you:
                </strong>{' '}
                Monitoring apps don't teach digital literacy. VPNs don't explain
                phishing. Antivirus software doesn't build confidence.
              </p>

              <div className="bg-white rounded-2xl p-8 border-2 border-[#631bb7] border-opacity-30 my-8" data-id="element-1308">
                <p className="text-xl text-[#111111] font-semibold text-center mb-0" data-id="element-1309">
                  Your family doesn't need another app watching them.
                  <br data-id="element-1310" />
                  <span className="text-[#631bb7]" data-id="element-1311">
                    They need someone who can finally explain this stuff in a
                    way that makes sense.
                  </span>
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Solution/Benefit Section */}
      <section className="w-full bg-white py-16 md:py-24 border-t border-gray-200" data-id="element-1312">
        <div className="max-w-7xl mx-auto px-6 md:px-12" data-id="element-1313">
          <div className="max-w-4xl mx-auto" data-id="element-1314">
            <h2 className="text-3xl md:text-4xl font-bold text-[#111111] mb-4 text-center" data-id="element-1315">
              Meet Your Family's Digital Fiduciary
            </h2>
            <p className="text-xl text-gray-600 text-center mb-6" data-id="element-1316">
              Caveo is different because we're not selling you surveillance
              disguised as security.
            </p>
            <p className="text-lg text-gray-600 text-center mb-12 max-w-2xl mx-auto" data-id="element-1317">
              We're the world's first{' '}
              <strong className="text-[#111111]" data-id="element-1318">Digital Fiduciary</strong> —
              which means we have a legal duty to put your family's interests
              first, not push products that pay us commissions.
            </p>

            <h3 className="text-2xl font-bold text-[#111111] mb-8 text-center" data-id="element-1319">
              What that means for your family:
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12" data-id="element-1320">
              <div className="bg-[#f8f8f8] rounded-2xl p-8 border-2 border-gray-200" data-id="element-1321">
                <div className="w-12 h-12 bg-[#631bb7] bg-opacity-10 rounded-xl flex items-center justify-center mb-4" data-id="element-1322">
                  <BookOpenIcon className="w-6 h-6 text-[#631bb7]" data-id="element-1323" />
                </div>
                <h3 className="text-xl font-bold text-[#111111] mb-3" data-id="element-1324">
                  Education that actually sticks
                </h3>
                <p className="text-gray-600 leading-relaxed" data-id="element-1325">
                  Not 50-page PDFs nobody reads. Not YouTube tutorials your
                  parents won't watch. Just 28 plain-English lessons that teach
                  one critical skill at a time, sequenced by what threatens your
                  family most.
                </p>
              </div>

              <div className="bg-[#f8f8f8] rounded-2xl p-8 border-2 border-gray-200" data-id="element-1326">
                <div className="w-12 h-12 bg-[#631bb7] bg-opacity-10 rounded-xl flex items-center justify-center mb-4" data-id="element-1327">
                  <MessageCircleHeartIcon className="w-6 h-6 text-[#631bb7]" data-id="element-1328" />
                </div>
                <h3 className="text-xl font-bold text-[#111111] mb-3" data-id="element-1329">
                  Honest recommendations
                </h3>
                <p className="text-gray-600 leading-relaxed" data-id="element-1330">
                  We'll tell you that most people don't need a standalone VPN.
                  We'll explain why password reuse is more dangerous than any
                  virus. We'll help you cancel the three security subscriptions
                  you're overpaying for —{' '}
                  <strong data-id="element-1331">saving your family $500-1,000/year.</strong>
                </p>
              </div>

              <div className="bg-[#f8f8f8] rounded-2xl p-8 border-2 border-gray-200" data-id="element-1332">
                <div className="w-12 h-12 bg-[#631bb7] bg-opacity-10 rounded-xl flex items-center justify-center mb-4" data-id="element-1333">
                  <LayoutDashboardIcon className="w-6 h-6 text-[#631bb7]" data-id="element-1334" />
                </div>
                <h3 className="text-xl font-bold text-[#111111] mb-3" data-id="element-1335">
                  One family hub
                </h3>
                <p className="text-gray-600 leading-relaxed" data-id="element-1336">
                  No more being "family IT support" across four time zones. Your
                  parents get the answers. You get visibility. Everyone
                  maintains dignity. Nobody loses their Friday nights.
                </p>
              </div>

              <div className="bg-[#f8f8f8] rounded-2xl p-8 border-2 border-gray-200" data-id="element-1337">
                <div className="w-12 h-12 bg-[#631bb7] bg-opacity-10 rounded-xl flex items-center justify-center mb-4" data-id="element-1338">
                  <UsersIcon className="w-6 h-6 text-[#631bb7]" data-id="element-1339" />
                </div>
                <h3 className="text-xl font-bold text-[#111111] mb-3" data-id="element-1340">
                  Role-based access
                </h3>
                <p className="text-gray-600 leading-relaxed" data-id="element-1341">
                  Parents learn at their own pace. Adult children can see
                  progress without hovering. Everyone gets appropriate guidance
                  for their tech comfort level.
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto" data-id="element-1342">
              <div className="bg-[#631bb7] bg-opacity-5 rounded-xl p-6 border border-[#631bb7] border-opacity-20" data-id="element-1343">
                <p className="text-sm font-semibold text-[#631bb7] uppercase tracking-wide mb-2" data-id="element-1344">
                  For Adult Children
                </p>
                <p className="text-[#111111] font-medium" data-id="element-1345">
                  <strong data-id="element-1346">Freedom.</strong> Stop answering the same tech
                  questions every Sunday. Stop worrying about the calls you'll
                  get when something goes wrong.
                </p>
              </div>
              <div className="bg-[#631bb7] bg-opacity-5 rounded-xl p-6 border border-[#631bb7] border-opacity-20" data-id="element-1347">
                <p className="text-sm font-semibold text-[#631bb7] uppercase tracking-wide mb-2" data-id="element-1348">
                  For Parents
                </p>
                <p className="text-[#111111] font-medium" data-id="element-1349">
                  <strong data-id="element-1350">Dignity.</strong> Learn without feeling judged. Get
                  clear answers without bothering your kids about "simple"
                  questions.
                </p>
              </div>
              <div className="bg-[#631bb7] bg-opacity-5 rounded-xl p-6 border border-[#631bb7] border-opacity-20" data-id="element-1351">
                <p className="text-sm font-semibold text-[#631bb7] uppercase tracking-wide mb-2" data-id="element-1352">
                  For Everyone
                </p>
                <p className="text-[#111111] font-medium" data-id="element-1353">
                  <strong data-id="element-1354">Save $500-1,000/year.</strong> Consolidate
                  subscriptions. Build lasting capability, not temporary fixes.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Social Proof/Trust Section */}
      <section className="w-full bg-[#1a0647] py-16 md:py-24" data-id="element-1355">
        <div className="max-w-7xl mx-auto px-6 md:px-12" data-id="element-1356">
          <div className="max-w-4xl mx-auto" data-id="element-1357">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-8 text-center" data-id="element-1358">
              Built by Someone Living This Same Story
            </h2>

            <div className="bg-white bg-opacity-10 rounded-2xl p-8 mb-12" data-id="element-1359">
              <p className="text-lg text-white text-opacity-90 leading-relaxed mb-6" data-id="element-1360">
                Jason Phillips spent 15 years at companies like Asana, Drift,
                and Quantcast watching the digital literacy gap widen between
                generations.
              </p>
              <p className="text-lg text-white text-opacity-90 leading-relaxed mb-6" data-id="element-1361">
                Then his father lost money to cyber fraud. Then his cousin was
                impersonated on Facebook.
              </p>
              <p className="text-lg text-white text-opacity-90 leading-relaxed mb-6" data-id="element-1362">
                He looked at Aura ($384/year), Life360 ($300/year), and McAfee+
                ($109/year) and realized they were all solving the wrong
                problem.
              </p>
              <p className="text-xl text-[#00f4f8] font-semibold mb-6" data-id="element-1363">
                The market was selling monitoring. Families needed education.
              </p>
              <p className="text-lg text-white text-opacity-90 leading-relaxed" data-id="element-1364">
                So he built Caveo — the first digital security platform that
                puts teaching before tracking, designed specifically for the
                sandwich generation managing tech for aging parents.
              </p>
            </div>

            <h3 className="text-xl font-semibold text-white text-opacity-80 mb-6 text-center" data-id="element-1365">
              From Families Like Yours:
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-1366">
              <div className="bg-white rounded-2xl p-6" data-id="element-1367">
                <p className="text-gray-600 leading-relaxed mb-4 italic" data-id="element-1368">
                  "I bought this for my parents. Within two weeks, Mom stopped
                  calling me about every password reset. That alone was worth
                  way more than $6."
                </p>
                <p className="font-bold text-[#111111]" data-id="element-1369">— Sarah M., 42</p>
                <p className="text-sm text-gray-500" data-id="element-1370">
                  Managing care for aging parents
                </p>
              </div>
              <div className="bg-white rounded-2xl p-6" data-id="element-1371">
                <p className="text-gray-600 leading-relaxed mb-4 italic" data-id="element-1372">
                  "Caveo told me I could cancel two subscriptions I didn't need.
                  When does a security company ever do that?"
                </p>
                <p className="font-bold text-[#111111]" data-id="element-1373">— David R.</p>
                <p className="text-sm text-gray-500" data-id="element-1374">Gen X executive</p>
              </div>
              <div className="bg-white rounded-2xl p-6" data-id="element-1375">
                <p className="text-gray-600 leading-relaxed mb-4 italic" data-id="element-1376">
                  "My dad is 71 and actually understands two-factor
                  authentication now. I didn't think that was possible."
                </p>
                <p className="font-bold text-[#111111]" data-id="element-1377">— Jennifer K.</p>
                <p className="text-sm text-gray-500" data-id="element-1378">Daughter</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Feature Breakdown */}
      <section className="w-full bg-white py-16 md:py-24 border-t border-gray-200" data-id="element-1379">
        <div className="max-w-7xl mx-auto px-6 md:px-12" data-id="element-1380">
          <div className="max-w-4xl mx-auto" data-id="element-1381">
            <h2 className="text-3xl md:text-4xl font-bold text-[#111111] mb-12 text-center" data-id="element-1382">
              What Your Family Gets
            </h2>

            <div className="space-y-8" data-id="element-1383">
              <div className="flex items-start" data-id="element-1384">
                <div className="w-14 h-14 bg-[#631bb7] bg-opacity-10 rounded-2xl flex items-center justify-center mr-6 flex-shrink-0" data-id="element-1385">
                  <BookOpenIcon className="w-7 h-7 text-[#631bb7]" data-id="element-1386" />
                </div>
                <div data-id="element-1387">
                  <h3 className="text-xl font-bold text-[#111111] mb-2" data-id="element-1388">
                    The Education Platform
                  </h3>
                  <p className="text-gray-600 leading-relaxed" data-id="element-1389">
                    28 modules organized by risk (not alphabet soup). Everyone
                    starts with what matters most — password hygiene, two-factor
                    auth, backup basics. Delivered in plain English your parents
                    can understand without a tech degree.
                  </p>
                </div>
              </div>

              <div className="flex items-start" data-id="element-1390">
                <div className="w-14 h-14 bg-[#631bb7] bg-opacity-10 rounded-2xl flex items-center justify-center mr-6 flex-shrink-0" data-id="element-1391">
                  <LayoutDashboardIcon className="w-7 h-7 text-[#631bb7]" data-id="element-1392" />
                </div>
                <div data-id="element-1393">
                  <h3 className="text-xl font-bold text-[#111111] mb-2" data-id="element-1394">
                    The Family Dashboard
                  </h3>
                  <p className="text-gray-600 leading-relaxed" data-id="element-1395">
                    One place to see where everyone stands. Role-based access
                    means you can help without hovering. Parents maintain
                    dignity. Adult children get peace of mind.
                  </p>
                </div>
              </div>

              <div className="flex items-start" data-id="element-1396">
                <div className="w-14 h-14 bg-[#631bb7] bg-opacity-10 rounded-2xl flex items-center justify-center mr-6 flex-shrink-0" data-id="element-1397">
                  <MessageCircleHeartIcon className="w-7 h-7 text-[#631bb7]" data-id="element-1398" />
                </div>
                <div data-id="element-1399">
                  <h3 className="text-xl font-bold text-[#111111] mb-2" data-id="element-1400">
                    The Honest Advisor
                  </h3>
                  <p className="text-gray-600 leading-relaxed" data-id="element-1401">
                    Most people don't need a VPN. Most free antivirus works
                    fine. Most "monitoring" services are security theater. We'll
                    tell you what your family actually needs and help you cancel
                    what you don't — saving you hundreds per year.
                  </p>
                </div>
              </div>

              <div className="flex items-start" data-id="element-1402">
                <div className="w-14 h-14 bg-[#631bb7] bg-opacity-10 rounded-2xl flex items-center justify-center mr-6 flex-shrink-0" data-id="element-1403">
                  <LifeBuoyIcon className="w-7 h-7 text-[#631bb7]" data-id="element-1404" />
                </div>
                <div data-id="element-1405">
                  <h3 className="text-xl font-bold text-[#111111] mb-2" data-id="element-1406">
                    The Crisis Toolkit
                  </h3>
                  <p className="text-gray-600 leading-relaxed" data-id="element-1407">
                    When something goes wrong (because eventually it will),
                    nobody has to Google "what to do if..." We have step-by-step
                    response plans for every common threat — from locked
                    accounts to suspicious charges.
                  </p>
                </div>
              </div>

              <div className="flex items-start" data-id="element-1408">
                <div className="w-14 h-14 bg-[#631bb7] bg-opacity-10 rounded-2xl flex items-center justify-center mr-6 flex-shrink-0" data-id="element-1409">
                  <UsersIcon className="w-7 h-7 text-[#631bb7]" data-id="element-1410" />
                </div>
                <div data-id="element-1411">
                  <h3 className="text-xl font-bold text-[#111111] mb-2" data-id="element-1412">
                    Multi-Generational Design
                  </h3>
                  <p className="text-gray-600 leading-relaxed" data-id="element-1413">
                    Your teenagers learn different skills than your parents.
                    Your parents get different guidance than you do. Everyone
                    gets what they need at the level that makes sense.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="w-full bg-[#f8f8f8] py-16 md:py-24 border-t border-gray-200" data-id="element-1414">
        <div className="max-w-7xl mx-auto px-6 md:px-12" data-id="element-1415">
          <div className="max-w-3xl mx-auto text-center" data-id="element-1416">
            <h2 className="text-3xl md:text-4xl font-bold text-[#111111] mb-4" data-id="element-1417">
              Stop Paying for Surveillance.
              <br data-id="element-1418" />
              Start Building Confidence.
            </h2>
            <p className="text-xl text-gray-600 leading-relaxed mb-8" data-id="element-1419">
              Your family doesn't need another monitoring app. They need the
              patient tech expert they've always wanted.
            </p>

            <div className="bg-white rounded-2xl p-8 border-2 border-gray-200 mb-8" data-id="element-1420">
              <p className="text-lg text-gray-600 mb-6" data-id="element-1421">
                For less than one coffee per month, everyone gets:
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-left max-w-lg mx-auto mb-6" data-id="element-1422">
                {['28 risk-prioritized security lessons', 'One centralized family dashboard', 'Role-based access (support without hovering)', 'Honest guidance (even when it costs us a sale)', 'Crisis response plans', 'No affiliate bias. No upsells. No fear tactics.'].map((item, index) => <div key={index} className="flex items-start" data-id="element-1423">
                    <CheckIcon className="w-5 h-5 text-[#631bb7] mr-2 flex-shrink-0 mt-0.5" data-id="element-1424" />
                    <span className="text-[#111111]" data-id="element-1425">{item}</span>
                  </div>)}
              </div>
              <div className="text-center mb-6" data-id="element-1426">
                <span className="text-4xl font-extrabold text-[#111111]" data-id="element-1427">
                  $5.99
                </span>
                <span className="text-gray-500 text-lg" data-id="element-1428">/month</span>
                <span className="text-gray-500 text-lg ml-2" data-id="element-1429">
                  for your entire family
                </span>
                <p className="text-gray-500 text-sm mt-1" data-id="element-1430">
                  No contract. Cancel anytime.
                </p>
              </div>
              <Link to="/onboarding" className="bg-[#631bb7] text-white px-8 py-4 rounded-lg font-bold inline-flex items-center justify-center no-underline hover:bg-[#521099] transition-all shadow-md w-full sm:w-auto" data-id="element-1431">
                Start Your Family's Digital Education
              </Link>
            </div>

            {/* Math breakdown */}
            <div className="bg-[#1a0647] rounded-2xl p-6 mb-8" data-id="element-1432">
              <div className="flex items-center justify-center mb-3" data-id="element-1433">
                <CalculatorIcon className="w-5 h-5 text-[#00f4f8] mr-2" data-id="element-1434" />
                <span className="font-bold text-white" data-id="element-1435">Do the math:</span>
              </div>
              <div className="text-white text-opacity-80 space-y-1 mb-4" data-id="element-1436">
                <p data-id="element-1437">
                  Aura ($384/year) + Life360 ($300/year) + McAfee+ ($109/year) ={' '}
                  <strong className="text-white" data-id="element-1438">$793/year</strong>
                </p>
              </div>
              <p className="text-[#00f4f8] font-bold text-xl" data-id="element-1439">
                Caveo: $72/year. Save $721. Get better education.
              </p>
            </div>

            <div className="bg-[#631bb7] bg-opacity-5 rounded-xl p-6 border border-[#631bb7] border-opacity-20 mb-6" data-id="element-1440">
              <p className="font-bold text-[#111111] mb-2" data-id="element-1441">The Caveo Promise</p>
              <p className="text-gray-600" data-id="element-1442">
                If we don't make your family feel more confident and capable in
                the first 30 days, we'll refund you immediately. No questions.
                No hassle.
              </p>
            </div>

            <p className="text-gray-500 text-sm" data-id="element-1443">
              Join 2,000+ families who stopped paying for surveillance and
              started building capability.
            </p>
          </div>
        </div>
      </section>

      <Footer data-id="element-1444" />
    </div>;
}